import React, { useState } from 'react';
import { logMapBaseLayerTapped } from '@alltrails/analytics/amplitudeFunctions';
import { wrapEventHandler } from '../../utils/handlers';
import { activities } from '../../utils/activity_util';
import Button from '@alltrails/shared/denali/components/Button';
import CustomMapSourceModal from '../CustomMapSourceModal';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as styles from './styles/styles.module.scss';
import proBadgeSvg from './pro-badge.svg';
import { usePageContext } from '../shared/Page/Page';
import { getPageTitle } from '../../utils/RoutePath';
import { MAP, TRAIL } from '../../utils/constants/MapType';

const HeatmapCheckbox = ({ layerKey, handleHeatmapClick, disabled, checked }) => {
  return (
    <>
      <input
        checked={checked}
        onClick={event => event.stopPropagation()}
        onChange={() => {
          handleHeatmapClick(layerKey);
        }}
        disabled={disabled}
        className={styles.heatmapCheckbox}
        type="checkbox"
      />
      <span className={styles.checkmark} />
    </>
  );
};

const Card = ({
  adminLayout,
  card,
  handleCardClick,
  handleHeatmapClick,
  mobileBrowser,
  handleActivityFilterChange,
  selectedFilterActivity,
  adminCustomizationSettings,
  handleAdminCustomizationSettingsChange,
  enabledOverlays
}) => {
  const context = usePageContext();

  let modifierKey = 'Alt';
  const [showCustomMapSourceModal, toggleCustomMapSourceModal] = useState(false);

  try {
    const { platform } = window.navigator;
    if (platform.toLowerCase().indexOf('mac') > -1) {
      modifierKey = 'Opt';
    }
  } catch {
    // noop
  }

  const onSelectChange = e => {
    handleActivityFilterChange(e);
  };

  const renderDropdownSelect = () => {
    const options = activities.map(activity => {
      return (
        <option key={`${card.key}-option-${activity.value}`} value={activity.value}>
          {activity.label}
        </option>
      );
    });
    options.unshift(
      <option key={`${card.key}-option-all`} value="">
        All
      </option>
    );
    return (
      <select value={selectedFilterActivity} onChange={onSelectChange} className={styles.activitySelect}>
        {options}
      </select>
    );
  };

  const handleCustomMapSourceModal = (e, settingsKey, source) => {
    e.stopPropagation();
    handleAdminCustomizationSettingsChange(settingsKey, source.url);
    toggleCustomMapSourceModal(false);
  };

  const renderAdminSourceEdit = () => {
    // Only offer edit option when layer is turned off:
    if (card.selected) {
      return null;
    }
    const settingsKey = card.key === 'adminOverlay' ? 'overlaySourceUrl' : 'basemapSourceUrl';
    const sourceUrl = adminCustomizationSettings[settingsKey];
    if (showCustomMapSourceModal) {
      return (
        <CustomMapSourceModal
          source={{ url: sourceUrl }}
          handleSubmit={(e, source) => handleCustomMapSourceModal(e, settingsKey, source)}
          closeModal={wrapEventHandler(e => toggleCustomMapSourceModal(false))}
        />
      );
    }
    const label = sourceUrl && sourceUrl.length > 0 ? 'Edit URL' : 'Add URL';
    return (
      <Button
        text={label}
        className={styles.sourceEdit}
        onClick={wrapEventHandler(e => toggleCustomMapSourceModal(true))}
        testId={label}
        variant="primary"
      />
    );
  };

  const renderOptionElement = () => {
    switch (card.key) {
      case 'recordingsAll':
      case 'recordingsPop':
        return renderDropdownSelect();
      case 'adminBasemap':
      case 'adminOverlay':
        return renderAdminSourceEdit();
    }
    return null;
  };

  const renderHeatmapCheckbox = () => {
    if (!(card.hasHeatmap && adminLayout)) {
      return null;
    }

    const layerKey = `${card.key}Heatmap`;

    return (
      <HeatmapCheckbox
        layerKey={layerKey}
        disabled={!card.selected}
        handleHeatmapClick={handleHeatmapClick}
        checked={enabledOverlays.includes(layerKey)}
      />
    );
  };

  const onCardClick = e => {
    logMapBaseLayerTapped({
      page: getPageTitle(window.location.pathname),
      map_id: context.initialSelectedObject?.type === MAP ? context.initialSelectedObject.ID : undefined,
      trail_id: context.initialSelectedObject?.type === TRAIL ? context.initialSelectedObject.ID : undefined
    });

    // Ignore if admin is using sub dropdown on the card, or a popup is being shown
    if (showCustomMapSourceModal || (e.target && e.target.tagName == 'SELECT')) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    handleCardClick(card, e);
  };

  const optionElement = renderOptionElement();
  const terrainIndicator =
    card.terrainEnabled && !mobileBrowser ? <span className={styles.terrainAvailable}>{StringConstantsMixin.strings.TERRAIN_AVAILABLE}</span> : null;
  const proBadge =
    card.proRequired && !adminLayout ? (
      <img alt={StringConstantsMixin.strings.NAVIGATION.PRO_BADGE_ALT_TEXT} className={styles.proBadge} src={proBadgeSvg} />
    ) : null;
  const keyboardShortcut =
    adminLayout && !mobileBrowser ? (
      <h4 className={styles.keyboardShortcut}>{card.keyboardShortcut ? `${modifierKey}+${card.keyboardShortcut}` : '\u00A0'}</h4>
    ) : null;

  return (
    <div className={styles.cardContainer}>
      <div
        data-testid={card.title}
        key={`key-${card.key}`}
        className={`${styles.card} ${card.selected ? styles.selected : ''}`}
        onClick={wrapEventHandler(e => {
          onCardClick(card, e);
        })}
      >
        {terrainIndicator}
        {optionElement}
        {renderHeatmapCheckbox()}
        {proBadge}
        <img src={card.imgSrc} />
      </div>
      <h3 className={styles.cardTitle}>{card.title}</h3>
      {keyboardShortcut}
    </div>
  );
};

export default Card;
